import React, { useEffect, useRef, useState } from "react";
import RailGroup from "../record/RailGroup";
import useRoom, { roomsApi } from "./useRoom";
import { Switch, DragDots, Check, useRunner, useFileUrl } from "./parts";
import { SECTIONS, SECTION_LABEL, SECTION_ACTION, statsLine, statChips, itemRow, isDemoItem, eventWords, personLine, personName } from "./roomModel.mjs";
import RecapDialog from "./RecapDialog";
import EmailTrimDialog from "./EmailTrimDialog";
import { LibraryPicker, EditDeckDialog, EditPricingDialog, EditAgreementDialog, PandaDocDialog, EmailPicker } from "./Dialogs";
import DateField from "../datepicker/DateField";

/*
 * THE PROSPECT ROOM ON THE RECORD (RoomPanel board): the company's one page for
 * this prospect, edited in place. The link with Copy, on/off, Preview as
 * <company> and Re-issue; who opened it and where they spent the time; the two
 * lines at the top of the page; and every item, by section, with Edit, Remove
 * and drag to reorder. A section's own link adds to it.
 *
 * A rail group like the others (record/RailGroup.js): one line closed, the panel
 * open. It is the company's, so it reads company.id, and every surface on the
 * record that adds to the room shares its state through useRoom.
 *
 * Props: record (company, timeline.rows), sheet (phone), focus (open and
 * scroll into view: the Prospect rooms page opens a record this way)
 */

/** A timeline email row → what the trim dialog needs. */
export function emailFromRow(row, host = "Gabriel") {
  const out = (row.direction || (row.metadata || {}).direction) === "outbound";
  const name = (p) => (p ? [p.first_name, p.last_name].filter(Boolean).join(" ") || p.email || "" : "");
  const holders = (row.holders || []).map(name).filter(Boolean);
  return {
    activity_id: row.id,
    body: row.body || "",
    subject: row.title || "",
    at: row.occurred_at,
    from: out ? { name: host, us: true } : { name: name(row.sender) || holders[0] || "Them", us: false },
    to: out ? holders : [host],
  };
}

export default function RoomGroup({ record, sheet = false, focus = false, openSignal = 0 }) {
  const company = record.company;
  const companyId = company && company.id;
  const { data, error } = useRoom(companyId);
  const [open, setOpen] = useState(!!focus);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const run = useRunner(setBusy, setErr);
  const ref = useRef(null);

  useEffect(() => {
    if (focus && ref.current) ref.current.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [focus, data]);
  useEffect(() => {
    if (openSignal && !sheet && ref.current) ref.current.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [openSignal, sheet]);

  if (!companyId) return null;
  const room = data && data.room;
  const name = company.name || "them";
  const stats = data && data.stats;
  const total = room ? SECTIONS.reduce((n, s) => n + ((data.sections[s] || []).length), 0) : 0;

  const summary = !data ? (error ? "Could not load" : "…") : !room ? "No room yet" : !room.enabled ? "Off · the link shows “not available”" : stats && stats.opens ? statsLine(stats) : total ? `${total} ${total === 1 ? "item" : "items"} · not opened yet` : "Empty · nothing shared yet";

  const close = () => setDialog(null);
  const rows = (record.timeline && record.timeline.rows) || [];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(room.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setErr("Copy did not work in this browser: select the link and copy it.");
    }
  };

  const preview = () =>
    run(async () => {
      // Opened first, filled after: a pop-up blocker allows a window opened inside the click.
      const w = window.open("about:blank", "_blank");
      const { url } = await roomsApi.preview(companyId);
      if (w) w.location.href = url;
      else window.location.href = url;
    });

  const sectionAction = (section) => {
    switch (section) {
      case "decks":
        return setDialog({ kind: "picker" });
      case "pricing":
        return run(() => roomsApi.add(companyId, { section: "pricing" }));
      case "recaps":
        return setDialog({ kind: "pick-email", followUps: true });
      case "agreement":
        return setDialog({ kind: "pandadoc" });
      case "conversation":
        return setDialog({ kind: "pick-email", followUps: false });
      default:
        return null;
    }
  };

  const edit = (item) => {
    switch (item.section) {
      case "decks":
        return setDialog({ kind: "edit-deck", item });
      case "pricing":
        return setDialog({ kind: "edit-pricing", item });
      case "recaps":
        return setDialog({ kind: "edit-recap", item });
      case "agreement":
        return setDialog({ kind: "edit-agreement", item });
      case "conversation":
        return setDialog({ kind: "edit-email", item });
      default:
        return null;
    }
  };

  const remove = (item) => {
    const row = itemRow(item, { host: data.host });
    if (isDemoItem(item)) {
      if (!window.confirm(`Take the demo dashboard off ${name}’s room? Their demo link stops working within two minutes, even in a tab they already have open.`)) return;
      return run(() => roomsApi.removeItem(companyId, item.id));
    }
    if (!window.confirm(`Take “${row.title}” off ${name}’s room? It leaves the page the next time they open it.`)) return;
    run(() => roomsApi.removeItem(companyId, item.id));
  };

  const reissue = () => {
    if (!window.confirm(`Make a new link for ${name}’s room? The old link stops working at once: anyone who has it sees “This page isn’t available right now”.`)) return;
    run(() => roomsApi.reissue(companyId));
  };

  return (
    <div ref={ref} className="rm" style={{ scrollMarginTop: 16 }}>
      <RailGroup id="room" title="Prospect Room" count={room && total ? total : null} summary={summary} open={open} onToggle={setOpen} sheet={sheet} openSignal={openSignal}>
        {!data ? (
          <p className="rm-sub">{error || "Loading…"}</p>
        ) : !room ? (
          <div className="rm-empty">
            <span className="rm-sub">
              One private page for {name} with everything you choose to share: decks, pricing, call recaps, the agreement, emails. Nothing goes on it unless you add it.
            </span>
            <button type="button" className="rm-btn rm-btn--primary" style={{ alignSelf: "flex-start" }} disabled={busy} onClick={() => run(() => roomsApi.create(companyId))}>
              Create the room
            </button>
            {err && <span className="rm-err">{err}</span>}
          </div>
        ) : (
          <Panel
            data={data}
            name={name}
            contacts={company.contacts}
            busy={busy}
            err={err}
            copied={copied}
            onCopy={copy}
            onPreview={preview}
            onReissue={reissue}
            onToggle={(on) => run(() => roomsApi.update(companyId, { enabled: on }))}
            onSaveTop={(patch) => roomsApi.update(companyId, patch)}
            onSection={sectionAction}
            onDemo={() => run(() => roomsApi.add(companyId, { section: "decks", demo: true }))}
            onEdit={edit}
            onRemove={remove}
            onOrder={(section, ids) => run(() => roomsApi.order(companyId, section, ids))}
          />
        )}
      </RailGroup>

      {dialog && dialog.kind === "picker" && <LibraryPicker companyId={companyId} companyName={name} onBoard={data.sections.decks} onClose={close} />}
      {dialog && dialog.kind === "pandadoc" && <PandaDocDialog companyId={companyId} onClose={close} />}
      {dialog && dialog.kind === "pick-email" && (
        <EmailPicker
          rows={rows}
          followUps={dialog.followUps}
          onClose={close}
          onPick={(row) =>
            setDialog(dialog.followUps ? { kind: "recap", activityId: row.id } : { kind: "trim", email: emailFromRow(row, data.host) })
          }
        />
      )}
      {dialog && dialog.kind === "recap" && <RecapDialog companyId={companyId} companyName={name} activityId={dialog.activityId} onClose={close} />}
      {dialog && dialog.kind === "trim" && <EmailTrimDialog companyId={companyId} companyName={name} email={dialog.email} onClose={close} />}
      {dialog && dialog.kind === "edit-deck" && <EditDeckDialog companyId={companyId} companyName={name} item={dialog.item} onClose={close} />}
      {dialog && dialog.kind === "edit-pricing" && <EditPricingDialog companyId={companyId} item={dialog.item} onClose={close} />}
      {dialog && dialog.kind === "edit-recap" && <RecapDialog companyId={companyId} companyName={name} item={dialog.item} onClose={close} />}
      {dialog && dialog.kind === "edit-agreement" && <EditAgreementDialog companyId={companyId} item={dialog.item} newer={!!data.newer_draft} onClose={close} />}
      {dialog && dialog.kind === "edit-email" && <EmailTrimDialog companyId={companyId} companyName={name} item={dialog.item} onClose={close} />}
    </div>
  );
}

function Panel({ data, name, contacts, busy, err, copied, onCopy, onPreview, onReissue, onToggle, onSaveTop, onSection, onDemo, onEdit, onRemove, onOrder }) {
  const { room, stats } = data;
  return (
    <div className="rm-panel">
      <div className="rm-panel__top">
        <span className="rm-label">{room.enabled ? "Anyone with the link can open it" : "Switched off"}</span>
        <Switch on={room.enabled} label="Room is on" disabled={busy} onChange={onToggle} />
      </div>
      {!room.enabled && <div className="rm-offnote">Room is off. The link shows “This page isn’t available right now.” Nothing on it is deleted.</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div className="rm-linkrow">
          <label htmlFor="rm-link" className="rm-sr">Room link</label>
          <input id="rm-link" readOnly className={`rm-input${room.enabled ? "" : " is-off"}`} value={room.display_url} onFocus={(e) => e.target.select()} />
          <button type="button" className="rm-btn rm-btn--primary" onClick={onCopy}>{copied ? "Copied" : "Copy"}</button>
        </div>
        <div className="rm-linkacts">
          <button type="button" className="rm-link" disabled={busy || !room.enabled} onClick={onPreview}>Preview as {name} ↗</button>
          <button type="button" className="rm-link" disabled={busy} onClick={onReissue}>Re-issue link</button>
          <span className="rm-meta">kills the old link</span>
        </div>
        <PersonLink companyId={data.company.id} contacts={contacts} people={data.people} />
      </div>

      <div className="rm-block">
        <div className="rm-stats">{statsLine(stats)}</div>
        {statChips(stats).length > 0 && (
          <div className="rm-chips">
            {statChips(stats).map((c, i) => (
              <span key={c.section} className={`rm-chip${i === 0 ? " rm-chip--hi" : ""}`} style={{ cursor: "default" }}>{c.text}</span>
            ))}
          </div>
        )}
        <PeopleBlock people={data.people} />
        <div className="rm-meta">Names come from their personal links; anyone else is told apart by browser. Opens from you are not counted.</div>
      </div>

      <TheirLogo companyId={data.company.id} room={room} name={name} />

      <TopOfPage room={room} name={name} host={data.host} onSave={onSaveTop} />

      <StepsBlock companyId={data.company.id} steps={data.steps} name={name} />

      {err && <span className="rm-err">{err}</span>}

      <div className="rm-block" style={{ paddingTop: 4, gap: 0 }}>
        {SECTIONS.map((s) => (
          <Section key={s} section={s} items={data.sections[s] || []} data={data} busy={busy} onAction={() => onSection(s)} onDemo={s === "decks" ? onDemo : null} onEdit={onEdit} onRemove={onRemove} onOrder={onOrder} />
        ))}
      </div>
    </div>
  );
}

/*
 * NEXT STEPS (migration 073): the plan both sides tick. Theirs show a "Mark done"
 * on the page; here he ticks any of them, renames, reassigns, dates, orders and
 * removes. Text saves on blur, everything else on change.
 */
function StepsBlock({ companyId, steps, name }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [title, setTitle] = useState("");
  const [owner, setOwner] = useState("them");
  const run = useRunner(setBusy, setErr);
  const list = steps || [];
  const done = list.filter((x) => x.done_at).length;
  const move = (i, d) => {
    const ids = list.map((x) => x.id);
    const [id] = ids.splice(i, 1);
    ids.splice(i + d, 0, id);
    run(() => roomsApi.orderSteps(companyId, ids));
  };
  const add = () =>
    run(async () => {
      await roomsApi.addStep(companyId, { title, owner });
      setTitle("");
    });
  return (
    <div className="rm-block" style={{ gap: 6 }}>
      <div className="rm-block__head">
        <b>Next steps</b>
        <span className="rm-meta">{list.length ? `${done} of ${list.length} done` : "none on the page"}</span>
      </div>
      {list.map((x, i) => (
        <div key={x.id} style={{ display: "flex", flexDirection: "column", gap: 4, padding: "6px 0", borderBottom: "1px solid var(--rm-row)" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              aria-label={`Done: ${x.title}`}
              checked={!!x.done_at}
              disabled={busy}
              style={{ width: 18, height: 18, accentColor: "#0A0A0A", flex: "none" }}
              onChange={(e) => run(() => roomsApi.editStep(companyId, x.id, { done: e.target.checked }))}
            />
            <StepTitle step={x} disabled={busy} onSave={(t) => run(() => roomsApi.editStep(companyId, x.id, { title: t }))} />
          </div>
          <div style={{ display: "flex", gap: 4, alignItems: "center", paddingLeft: 26, minWidth: 0 }}>
            <select
              className="rm-select"
              aria-label="Whose step"
              style={{ width: "auto", maxWidth: 110, padding: "1px 4px", fontSize: 12, borderWidth: 1.5 }}
              value={x.owner}
              disabled={busy}
              onChange={(e) => run(() => roomsApi.editStep(companyId, x.id, { owner: e.target.value }))}
            >
              <option value="us">Remotion</option>
              <option value="them">{name}</option>
            </select>
            <DateField
              aria-label="Due"
              placeholder="Due date"
              className="dp-trigger--sm"
              value={x.due_on ? String(x.due_on).slice(0, 10) : ""}
              disabled={busy}
              clearable
              quick={[{ label: "Tomorrow", days: 1 }, { label: "In a week", days: 7 }, { label: "In 2 weeks", days: 14 }]}
              onChange={(d) => run(() => roomsApi.editStep(companyId, x.id, { due_on: d || null }))}
            />
            <span style={{ flex: 1 }} />
            <button type="button" className="rm-btn rm-btn--quiet" aria-label="Move up" disabled={busy || i === 0} onClick={() => move(i, -1)}>↑</button>
            <button type="button" className="rm-btn rm-btn--quiet" aria-label="Move down" disabled={busy || i === list.length - 1} onClick={() => move(i, 1)}>↓</button>
            <button type="button" className="rm-btn rm-btn--quiet" aria-label={`Remove ${x.title}`} disabled={busy} onClick={() => run(() => roomsApi.removeStep(companyId, x.id))}>×</button>
          </div>
          {x.done_at && x.done_by && x.done_by !== "us" && (
            <span className="rm-hint" style={{ paddingLeft: 26 }}>Ticked by {x.done_by === "them" ? name : x.done_by} on their room</span>
          )}
        </div>
      ))}
      <div style={{ display: "flex", gap: 6, alignItems: "center", paddingTop: 4, flexWrap: "wrap" }}>
        <input
          className="rm-input"
          style={{ flex: "1 1 100%" }}
          placeholder="Add a step"
          maxLength={120}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && title.trim() && add()}
        />
        <select className="rm-select" aria-label="Whose step" style={{ width: "auto", flex: 1 }} value={owner} onChange={(e) => setOwner(e.target.value)}>
          <option value="them">{name}</option>
          <option value="us">Remotion</option>
        </select>
        <button type="button" className="rm-btn" disabled={busy || !title.trim()} onClick={add}>Add</button>
      </div>
      {!list.length && (
        <button type="button" className="rm-link" style={{ alignSelf: "flex-start" }} disabled={busy} onClick={() => run(() => roomsApi.standardSteps(companyId))}>
          Add the standard list
        </button>
      )}
      <span className="rm-hint">Theirs show a “Mark done” on the page; you get an alert when they tick one.</span>
      {err && <span className="rm-err">{err}</span>}
    </div>
  );
}

function StepTitle({ step, disabled, onSave }) {
  const [t, setT] = useState(step.title);
  useEffect(() => setT(step.title), [step.title]);
  return (
    <input
      className="rm-input"
      aria-label="Step"
      style={{ fontWeight: 600, borderWidth: 1.5, borderColor: "transparent", padding: "2px 6px", textDecoration: step.done_at ? "line-through" : "none", color: step.done_at ? "var(--rm-sub)" : undefined }}
      onFocus={(e) => (e.target.style.borderColor = "#0A0A0A")}
      value={t}
      maxLength={120}
      disabled={disabled}
      onChange={(e) => setT(e.target.value)}
      onBlur={(e) => {
        e.target.style.borderColor = "transparent";
        if (t.trim() && t.trim() !== step.title) onSave(t.trim());
      }}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
    />
  );
}

/*
 * "Copy a link for…": the same room, with that person's name on what they do
 * (migration 072). The link is made the first time and the same one after.
 */
function PersonLink({ companyId, contacts, people }) {
  const [who, setWho] = useState("");
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const run = useRunner(setBusy, setErr);
  const list = (contacts || []).filter((c) => c && c.kind !== "junk");
  if (!list.length) return null;
  const nameOf = (c) => [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email;
  const copy = () =>
    run(async () => {
      const res = await roomsApi.personLink(companyId, who);
      const url = res && res.person && res.person.url;
      if (!url) return;
      try {
        await navigator.clipboard.writeText(url);
        setMsg(`Copied ${res.person.name}’s link`);
      } catch {
        setMsg(url);
      }
      setTimeout(() => setMsg(null), 4000);
    });
  const has = new Set((people || []).filter((p) => p.contact_id).map((p) => p.contact_id));
  return (
    <div className="rm-field" style={{ gap: 6 }}>
      <label htmlFor="rm-person" className="rm-field__label" style={{ fontWeight: 500, fontSize: 12.5 }}>
        A link with their name on it
      </label>
      <div className="rm-linkrow">
        <select id="rm-person" className="rm-select" value={who} onChange={(e) => setWho(e.target.value)}>
          <option value="">Pick a person…</option>
          {list.map((c) => (
            <option key={c.id} value={c.id}>
              {nameOf(c)}
              {has.has(c.id) ? " · has a link" : ""}
            </option>
          ))}
        </select>
        <button type="button" className="rm-btn" disabled={!who || busy} onClick={copy}>Copy</button>
      </div>
      {(msg || err) && <span className={err ? "rm-err" : "rm-ok"}>{err || msg}</span>}
    </div>
  );
}

/* Who opened it and what each of them clicked, newest first. */
function PeopleBlock({ people }) {
  const [open, setOpen] = useState(null);
  const list = people || [];
  if (!list.length) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {list.map((p) => {
        const key = p.person_id || "anon";
        const isOpen = open === key;
        const shown = isOpen ? p.events : p.events.slice(0, 3);
        return (
          <div key={key} style={{ padding: "8px 0", borderTop: "1px solid var(--rm-row)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
              <b style={{ fontWeight: 600 }}>{personName(p)}</b>
              <span className="rm-meta" style={{ textAlign: "right" }}>{personLine(p)}</span>
            </div>
            {shown.length > 0 && (
              <ul style={{ margin: "4px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 2 }}>
                {shown.map((e) => (
                  <li key={e.id} className="rm-sub" style={{ display: "flex", gap: 8 }}>
                    <span style={{ flex: "none", color: "var(--rm-meta)", fontSize: 12 }}>
                      {new Date(e.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })}
                    </span>
                    <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{eventWords(e)}</span>
                  </li>
                ))}
              </ul>
            )}
            {p.events.length > 3 && (
              <button type="button" className="rm-link" style={{ marginTop: 4 }} onClick={() => setOpen(isOpen ? null : key)}>
                {isOpen ? "Show less" : `Show all ${p.events.length}`}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

/*
 * Their logo beside their name on the page (migration 071): looked up once from
 * Apollo or their website when the room is first opened here, or uploaded.
 */
function TheirLogo({ companyId, room, name }) {
  const src = useFileUrl(room.logo_file_id);
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [note, setNote] = useState(null);
  const run = useRunner(setBusy, setErr);
  const from = { apollo: "found on Apollo", website: "from their website", upload: "uploaded by you" }[room.logo_source] || null;
  return (
    <div className="rm-block" style={{ gap: 8 }}>
      <div className="rm-block__head">
        <b>Their logo</b>
        <span className="rm-meta">{room.logo_file_id ? from : room.logo_checked_at ? "none found" : "looking…"}</span>
      </div>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <span aria-hidden="true" style={{ width: 56, height: 56, flex: "none", border: "2px solid #0A0A0A", borderRadius: 10, background: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 6, color: "#8F8C83", fontSize: 11, textAlign: "center" }}>
          {src ? <img src={src} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} /> : "No logo"}
        </span>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", alignItems: "baseline" }}>
          <button type="button" className="rm-link rm-link--strong" disabled={busy} onClick={() => input.current && input.current.click()}>
            {room.logo_file_id ? "Replace" : "Upload"}
          </button>
          {room.logo_file_id && (
            <button type="button" className="rm-link" disabled={busy} onClick={() => run(() => roomsApi.removeLogo(companyId))}>Remove</button>
          )}
          <button
            type="button"
            className="rm-link"
            disabled={busy}
            onClick={() =>
              run(async () => {
                const res = await roomsApi.findLogo(companyId);
                setNote(res && res.found ? null : `No logo found for ${name}.`);
              })
            }
          >
            Look again
          </button>
        </div>
        <input
          ref={input}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          hidden
          onChange={(e) => {
            const f = e.target.files && e.target.files[0];
            if (f) run(() => roomsApi.uploadLogo(companyId, f));
            e.target.value = "";
          }}
        />
      </div>
      {(note || err) && <span className={err ? "rm-err" : "rm-hint"}>{err || note}</span>}
    </div>
  );
}

/*
 * The top of the page: the welcome note, the next step, and what the yellow
 * card's button does (migration 070): book a call, or tell him they are ready
 * ("Get an agreement." → "Yes, let's move forward" → his phone). Text saves on
 * blur; the button choice saves on click.
 */
const TEXT_FIELDS = ["welcome_note", "next_step", "next_button", "next_thanks"];

function TopOfPage({ room, name, host, onSave }) {
  const [vals, setVals] = useState(() => Object.fromEntries(TEXT_FIELDS.map((k) => [k, room[k] || ""])));
  const [saved, setSaved] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setVals(Object.fromEntries(TEXT_FIELDS.map((k) => [k, room[k] || ""])));
  }, [room.id, room.welcome_note, room.next_step, room.next_button, room.next_thanks]);

  const commit = async (patch, key) => {
    setErr(null);
    try {
      await onSave(patch);
      setSaved(key);
    } catch (e) {
      setErr(e.message);
    }
  };
  const save = (key) => {
    if (String(vals[key]).trim() === String(room[key] || "").trim()) return;
    commit({ [key]: vals[key] }, key);
  };
  const bind = (key) => ({
    value: vals[key],
    onChange: (e) => {
      const v = e.target.value;
      setVals((x) => ({ ...x, [key]: v }));
      setSaved(null);
    },
    onBlur: () => save(key),
  });

  const ready = room.next_action === "ready";
  const ok = <span className="rm-ok"><Check />Saved · {name} sees it on their next visit</span>;
  const pressed = room.ready_at
    ? `Pressed ${new Date(room.ready_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })}${room.ready_count > 1 ? ` · ${room.ready_count} clicks` : ""}`
    : "Not pressed yet";
  return (
    <div className="rm-block" style={{ gap: 10 }}>
      <div className="rm-block__head">
        <b>Top of the page</b>
        <span className="rm-meta">empty = the standard wording</span>
      </div>
      <div className="rm-field">
        <label htmlFor="rm-welcome">Welcome note</label>
        <textarea id="rm-welcome" className="rm-textarea" rows={3} maxLength={1200} placeholder="Everything we’ve shared with your team, in one place…" {...bind("welcome_note")} />
        {saved === "welcome_note" && ok}
      </div>
      <div className="rm-field">
        <label htmlFor="rm-next">Next step</label>
        <input id="rm-next" className="rm-input" maxLength={160} placeholder={ready ? "Ready to move forward?" : `Book a call with ${host}`} {...bind("next_step")} />
        {saved === "next_step" ? ok : <div className="rm-hint">One line, the heading of the yellow card.</div>}
      </div>
      <div className="rm-field">
        <span className="rm-field__label" id="rm-btn-label">The card’s button</span>
        <div className="rm-chips" role="radiogroup" aria-labelledby="rm-btn-label">
          {[
            ["book", `Book a call with ${host}`],
            ["ready", "They tell you they’re ready"],
          ].map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={(room.next_action || "book") === k}
              className={`rm-chip${(room.next_action || "book") === k ? " is-on" : ""}`}
              onClick={() => (room.next_action || "book") !== k && commit({ next_action: k }, "next_action")}
            >
              {label}
            </button>
          ))}
        </div>
        {saved === "next_action" ? ok : (
          <div className="rm-hint">
            {ready ? "One click alerts your phone and puts a note on this record. The header’s Book a call stays." : "Opens your booking page, set up for them."}
          </div>
        )}
      </div>
      {ready && (
        <>
          <div className="rm-field">
            <label htmlFor="rm-next-btn">Button</label>
            <input id="rm-next-btn" className="rm-input" maxLength={60} placeholder="Yes, let’s move forward" {...bind("next_button")} />
            {saved === "next_button" && ok}
          </div>
          <div className="rm-field">
            <label htmlFor="rm-next-thanks">After they press it</label>
            <input id="rm-next-thanks" className="rm-input" maxLength={240} placeholder={`Thanks. ${host} has been told and will be in touch shortly.`} {...bind("next_thanks")} />
            {saved === "next_thanks" ? ok : <div className="rm-hint">{pressed}</div>}
          </div>
        </>
      )}
      {err && <span className="rm-err">{err}</span>}
    </div>
  );
}

function Section({ section, items, data, busy, onAction, onDemo, onEdit, onRemove, onOrder }) {
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const ids = items.map((i) => i.id);
  const drop = (targetId) => {
    if (drag == null || drag === targetId) return;
    const without = ids.filter((id) => id !== drag);
    const at = without.indexOf(targetId);
    without.splice(at, 0, drag);
    onOrder(section, without);
  };
  const empty = {
    decks: "Nothing yet. Pick decks, case studies or examples from the library.",
    pricing: "Not on the room. Puts the quote on record (What we quoted) as a ladder.",
    recaps: "No recaps. Pick a follow-up email you sent after a call.",
    agreement: "Not on the room. Share the draft from the agreement card, or paste the PandaDoc link.",
    conversation: "No emails. Pick one from the timeline, or use Add to room on an email.",
  }[section];
  return (
    <div className="rm-section">
      <div className="rm-section__head">
        <span>
          <b>{SECTION_LABEL[section]}</b>
          {items.length > 0 && <span> · {items.length}</span>}
        </span>
        <span style={{ display: "inline-flex", gap: 14 }}>
          {/* The demo dashboard: one per room, its link lives only while it is here (src/rooms/demo.js). */}
          {onDemo && !items.some(isDemoItem) && (
            <button type="button" className="rm-link" disabled={busy} onClick={onDemo} title="Adds a private link to the demo dashboard. Remove it and their link stops working.">
              + Demo dashboard
            </button>
          )}
          <button type="button" className="rm-link" disabled={busy} onClick={onAction}>
            {section === "pricing" && !items.length ? "+ What we quoted" : SECTION_ACTION[section]}
          </button>
        </span>
      </div>
      {items.length === 0 && <div className="rm-section__empty">{empty}</div>}
      {items.map((it) => {
        const row = itemRow(it, { host: data.host });
        return (
          <div
            key={it.id}
            className={`rm-row${drag === it.id ? " is-dragging" : ""}${over === it.id && drag !== it.id ? " is-over" : ""}`}
            onDragOver={(e) => {
              if (drag == null) return;
              e.preventDefault();
              setOver(it.id);
            }}
            onDragLeave={() => setOver((o) => (o === it.id ? null : o))}
            onDrop={(e) => {
              e.preventDefault();
              drop(it.id);
              setDrag(null);
              setOver(null);
            }}
          >
            <button
              type="button"
              className="rm-drag"
              aria-label={`Drag to reorder ${row.title}`}
              draggable={items.length > 1}
              disabled={items.length < 2}
              onDragStart={(e) => {
                setDrag(it.id);
                e.dataTransfer.effectAllowed = "move";
                try {
                  e.dataTransfer.setData("text/plain", String(it.id));
                } catch {
                  /* some browsers refuse; the state above is what counts */
                }
              }}
              onDragEnd={() => {
                setDrag(null);
                setOver(null);
              }}
              onKeyDown={(e) => {
                // Keyboard reordering: arrows move the row one place.
                const i = ids.indexOf(it.id);
                if (e.key === "ArrowUp" && i > 0) {
                  e.preventDefault();
                  const next = [...ids];
                  next.splice(i, 1);
                  next.splice(i - 1, 0, it.id);
                  onOrder(section, next);
                }
                if (e.key === "ArrowDown" && i < ids.length - 1) {
                  e.preventDefault();
                  const next = [...ids];
                  next.splice(i, 1);
                  next.splice(i + 1, 0, it.id);
                  onOrder(section, next);
                }
              }}
            >
              <DragDots />
            </button>
            <div className="rm-row__body">
              <div className="rm-row__title" title={row.title}>{row.title}</div>
              <div className="rm-row__meta">
                <span title={row.meta}>{row.meta}</span>
                {row.edited && <span className="rm-pill rm-pill--quiet">edited</span>}
              </div>
            </div>
            <span className="rm-row__acts">
              <button type="button" className="rm-btn rm-btn--sm" disabled={busy} onClick={() => onEdit(it)}>Edit</button>
              <button type="button" className="rm-btn rm-btn--quiet" style={{ fontSize: 12, padding: "0 2px" }} disabled={busy} onClick={() => onRemove(it)}>Remove</button>
            </span>
          </div>
        );
      })}
    </div>
  );
}
